<script setup lang="ts">
import { defineProps } from "vue";
import {
  CalendarDayParams,
  ChineseCalendarAction,
  ChineseCalendarDate,
} from "@/types/calculators";
import DayCardComponent from "@/views/Calculators/ChineseCalendar/components/DayCardComponent.vue";
import { useI18n } from "vue-i18n";

defineProps<{
  cardTitle: string;
  dayParams: CalendarDayParams & Pick<ChineseCalendarDate, "actions">;
}>();

const { t } = useI18n();
</script>

<template>
  <DayCardComponent>
    <template #card-title>
      <p class="text-white text-base md:text-xl xl:text-2xl font-semibold px-2">
        {{ cardTitle }}
      </p>
    </template>
    <template #card-content>
      <!-- POSITIVE DOINGS -->
      <p class="card-content-row">
        {{ t("calculators.chinese-calendar-day.day-summary.pluses") }}
        <span>
          {{
            dayParams.actions
              .filter((a: ChineseCalendarAction) => a.is_positive)
              .map((a: ChineseCalendarAction) =>
                t(`calculators.chinese-calendar.doing.${a.doing}`)
              )
              .join(", ")
          }}
        </span>
      </p>
      <!-- NEGATIVE DOINGS -->
      <p class="card-content-row">
        {{ t("calculators.chinese-calendar-day.day-summary.minuses") }}
        <span>
          {{
            dayParams.actions
              .filter((a: ChineseCalendarAction) => !a.is_positive)
              .map((a: ChineseCalendarAction) =>
                t(`calculators.chinese-calendar.doing.${a.doing}`)
              )
              .join(", ")
          }}
        </span>
      </p>
      <!-- OFFICER -->
      <div class="my-2" v-if="dayParams.officer !== undefined">
        <p class="entity-title">
          {{ dayParams.officer.name }}
        </p>
        <p class="card-content-row">{{ dayParams.officer.desc }}</p>
      </div>
      <!-- LUNAR MANSION -->
      <div class="my-2" v-if="dayParams.lunar_mansion !== undefined">
        <p class="entity-title">
          {{ dayParams.lunar_mansion.name }}
        </p>
        <p class="card-content-row">{{ dayParams.lunar_mansion.desc }}</p>
      </div>
      <!-- COLLISIONS -->
      <div v-if="dayParams.collisions !== undefined">
        <div
          class="my-2"
          v-for="(collision, id) in dayParams.collisions"
          :key="'collision-' + id"
        >
          <p class="entity-title text-error-dark">
            {{ collision.name }}
          </p>
          <p class="card-content-row">{{ collision.desc }}</p>
        </div>
      </div>
      <!-- NEGATIVE SHA -->
      <div v-if="dayParams.negative_sha.length > 0">
        <div
          class="my-2"
          v-for="(negative_sha, id) in dayParams.negative_sha"
          :key="'negative-sha-' + id"
        >
          <p class="entity-title text-error-dark">
            {{ negative_sha.name }}
          </p>
          <p class="card-content-row">{{ negative_sha.desc }}</p>
        </div>
      </div>
      <!-- SYMBOL STARS -->
      <div v-if="dayParams.symbol_stars.length > 0" class="flex flex-col gap-1">
        <div
          class="my-2"
          v-for="(star, id) in dayParams.symbol_stars"
          :key="'star-' + id"
        >
          <p
            class="entity-title"
            :class="{
              'text-error-dark': !star.is_positive,
              'text-success': star.is_positive,
            }"
          >
            {{ star.star }}
          </p>
          <p class="card-content-row">{{ star.desc }}</p>
        </div>
      </div>
      <!-- MOON DAY -->
      <p class="card-content-row">
        <span>
          {{
            t("calculators.chinese-calendar-day.day-summary.moon-day", {
              moon_day: dayParams.moon_day,
            })
          }}
        </span>
      </p>
      <!-- RETROGRADE DAY -->
      <p class="card-content-row" v-if="dayParams.is_retrograde">
        <span>
          {{
            t("calculators.chinese-calendar-day.day-summary.retrograde.prefix")
          }}
        </span>
        {{
          t("calculators.chinese-calendar-day.day-summary.retrograde.content")
        }}
      </p>
    </template>
  </DayCardComponent>
</template>

<style scoped>
.card-content-row {
  @apply text-left font-medium text-base md:text-xl text-brown;
  span {
    @apply font-semibold;
  }
}

.entity-title {
  @apply font-semibold text-base md:text-xl text-left;
}
</style>
